import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

const loadScript = () => new Promise((resolve, reject) => {
  if (window.google?.accounts?.id) return resolve();
  let s = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
  if (!s) {
    s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }
  s.addEventListener('load', () => resolve(), { once: true });
  s.addEventListener('error', reject, { once: true });
});

// Google One Tap: the "Continue to air-brief.com with google.com" prompt for signed-out
// visitors. Requires the site's origin in the OAuth client's Authorized JavaScript origins.
const GoogleOneTap = () => {
  const { isAuthenticated, loading, loginWithGoogleCredential } = useAuth();
  const navigate = useNavigate();
  const prompted = useRef(false);
  // Latest callbacks without re-running the effect (re-running would cancel the prompt)
  const handlers = useRef({ loginWithGoogleCredential, navigate });
  handlers.current = { loginWithGoogleCredential, navigate };

  useEffect(() => {
    if (loading || isAuthenticated || !CLIENT_ID || prompted.current) return undefined;
    let cancelled = false;

    loadScript().then(() => {
      if (cancelled || !window.google?.accounts?.id) return;
      prompted.current = true;
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: async ({ credential }) => {
          const result = await handlers.current.loginWithGoogleCredential(credential);
          if (result.success) handlers.current.navigate('/dashboard');
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin',
        itp_support: true,
        use_fedcm_for_prompt: true,
      });
      window.google.accounts.id.prompt();
    }).catch(() => {});

    return () => {
      cancelled = true;
      window.google?.accounts?.id?.cancel();
    };
  }, [loading, isAuthenticated]);

  return null;
};

export default GoogleOneTap;
